import React, { useState, useEffect } from 'react';
import { ChevronDown, Search, Users, Plus, Moon, Sun, Calendar, Paperclip, MessageSquare, CheckSquare, MoreVertical, Hash, User, Clock, Star, Menu, X, ArrowRight, TrendingUp, Zap, Award, Target } from 'lucide-react';

const App = () => {
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });
  const [tasks, setTasks] = useState(() => {
    const saved = localStorage.getItem('tasks');
    return saved ? JSON.parse(saved) : [
      {
        id: 1,
        title: 'Design new landing page',
        description: 'Create stunning hero section with animations',
        assignee: { name: 'Sarah Chen', avatar: 'SC' },
        priority: 'high',
        dueDate: '2024-01-15',
        comments: 3,
        attachments: 2,
        checklist: [
          { id: 1, text: 'Create wireframes', completed: true },
          { id: 2, text: 'Design mockups', completed: false },
          { id: 3, text: 'Get feedback', completed: false }
        ],
        lane: 'todo'
      },
      {
        id: 2,
        title: 'Implement authentication',
        description: 'Add OAuth with GitHub and Google',
        assignee: { name: 'Mike Johnson', avatar: 'MJ' },
        priority: 'medium',
        dueDate: '2024-01-20',
        comments: 1,
        attachments: 0,
        checklist: [
          { id: 1, text: 'Setup auth provider', completed: true },
          { id: 2, text: 'Create login form', completed: true },
          { id: 3, text: 'Add session management', completed: false }
        ],
        lane: 'inprogress'
      },
      {
        id: 3,
        title: 'Deploy to production',
        description: 'Setup CI/CD pipeline',
        assignee: { name: 'Alex Kim', avatar: 'AK' },
        priority: 'low',
        dueDate: '2024-01-25',
        comments: 5,
        attachments: 1,
        checklist: [
          { id: 1, text: 'Configure GitHub Actions', completed: true },
          { id: 2, text: 'Setup staging environment', completed: true },
          { id: 3, text: 'Deploy to production', completed: true }
        ],
        lane: 'done'
      }
    ];
  });
  const [activeView, setActiveView] = useState('board');
  const [expandedCard, setExpandedCard] = useState(null);
  const [showNewTask, setShowNewTask] = useState(false);
  const [newTaskLane, setNewTaskLane] = useState('todo');
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [draggedTask, setDraggedTask] = useState(null);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskDescription, setNewTaskDescription] = useState('');
  const [newTaskPriority, setNewTaskPriority] = useState('medium');
  const [newTaskAssignee, setNewTaskAssignee] = useState('Unassigned');

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    localStorage.setItem('tasks', JSON.stringify(tasks));
  }, [tasks]);

  const TaskCard = ({ task }) => {
    const priorityColors = {
      high: 'bg-gradient-to-r from-red-500 to-pink-500',
      medium: 'bg-gradient-to-r from-yellow-500 to-orange-500',
      low: 'bg-gradient-to-r from-green-500 to-emerald-500'
    };

    const handleDragStart = (e) => {
      setDraggedTask(task);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', task.id);
    };

    const handleDragEnd = () => {
      setDraggedTask(null);
    };

    return (
      <div
        draggable
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        className={`${darkMode ? 'bg-gray-800/90' : 'bg-white/95'} backdrop-blur-md rounded-xl p-4 mb-3 shadow-xl hover:shadow-2xl transform hover:scale-105 hover:-translate-y-1 transition-all duration-300 cursor-pointer border border-white/20 ${draggedTask?.id === task.id ? 'opacity-50 scale-95' : ''} group relative overflow-hidden`}
        onClick={() => setExpandedCard(expandedCard === task.id ? null : task.id)}
      >
        {/* Gradient overlay on hover */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-pink-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
        
        <div className="flex items-start justify-between mb-3 relative z-10">
          <h3 className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'} text-sm leading-tight group-hover:bg-gradient-to-r group-hover:from-purple-600 group-hover:to-pink-600 group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300`}>
            {task.title}
          </h3>
          <div className="flex items-center space-x-2">
            <span className={`px-2 py-1 rounded-full text-xs font-bold text-white ${priorityColors[task.priority]} shadow-lg`}>
              {task.priority.toUpperCase()}
            </span>
            <MoreVertical className={`w-4 h-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'} hover:text-gray-700 cursor-pointer transition-colors`} />
          </div>
        </div>
        
        <p className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-3 line-clamp-2 group-hover:text-gray-700 dark:group-hover:text-gray-200 transition-colors`}>
          {task.description}
        </p>

        <div className="flex items-center justify-between mb-3 relative z-10">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-xs font-bold shadow-lg">
              {task.assignee.avatar}
            </div>
            <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'} group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors`}>
              {task.assignee.name}
            </span>
          </div>
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-1">
              <Calendar className="w-3 h-3 text-gray-400 group-hover:text-purple-500 transition-colors" />
              <span className="text-xs text-gray-500 group-hover:text-purple-600 transition-colors">{task.dueDate}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between relative z-10">
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-1 group-hover:scale-110 transition-transform">
              <MessageSquare className="w-3 h-3 text-gray-400 group-hover:text-blue-500 transition-colors" />
              <span className="text-xs text-gray-500 group-hover:text-blue-600 transition-colors">{task.comments}</span>
            </div>
            <div className="flex items-center space-x-1 group-hover:scale-110 transition-transform">
              <Paperclip className="w-3 h-3 text-gray-400 group-hover:text-green-500 transition-colors" />
              <span className="text-xs text-gray-500 group-hover:text-green-600 transition-colors">{task.attachments}</span>
            </div>
            <div className="flex items-center space-x-1 group-hover:scale-110 transition-transform">
              <CheckSquare className="w-3 h-3 text-gray-400 group-hover:text-purple-500 transition-colors" />
              <span className="text-xs text-gray-500 group-hover:text-purple-600 transition-colors">
                {task.checklist.filter(item => item.completed).length}/{task.checklist.length}
              </span>
            </div>
          </div>
        </div>

        {expandedCard === task.id && (
          <div className="mt-4 pt-4 border-t border-gray-200/50 animate-fadeIn">
            <div className="space-y-2 mb-4">
              {task.checklist.map(item => (
                <div key={item.id} className="flex items-center space-x-2 group/check">
                  <input
                    type="checkbox"
                    checked={item.completed}
                    onChange={() => {
                      const newTasks = tasks.map(t => {
                        if (t.id === task.id) {
                          return {
                            ...t,
                            checklist: t.checklist.map(c => 
                              c.id === item.id ? { ...c, completed: !c.completed } : c
                            )
                          };
                        }
                        return t;
                      });
                      setTasks(newTasks);
                    }}
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span className={`text-sm ${item.completed ? 'line-through text-gray-500' : darkMode ? 'text-white' : 'text-gray-900'} group-hover/check:text-purple-600 transition-colors`}>
                    {item.text}
                  </span>
                </div>
              ))}
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                placeholder="Add comment..."
                className={`flex-1 px-3 py-2 rounded-lg ${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900'} text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:scale-105 transition-all duration-300`}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && e.target.value) {
                    const newTasks = tasks.map(t => 
                      t.id === task.id ? { ...t, comments: t.comments + 1 } : t
                    );
                    setTasks(newTasks);
                    e.target.value = '';
                  }
                }}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  const Lane = ({ title, status, tasks: laneTasks }) => {
    const handleDragOver = (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = (e) => {
      e.preventDefault();
      if (draggedTask) {
        const newTasks = tasks.map(task => 
          task.id === draggedTask.id ? { ...task, lane: status } : task
        );
        setTasks(newTasks);
        setDraggedTask(null);
      }
    };

    const handleLongPress = () => {
      const newTask = {
        id: Date.now(),
        title: `New Epic - ${title}`,
        description: 'Auto-generated epic with cascading tasks',
        assignee: { name: 'Unassigned', avatar: '?' },
        priority: 'medium',
        dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        comments: 0,
        attachments: 0,
        checklist: [
          { id: 1, text: 'Define requirements', completed: false },
          { id: 2, text: 'Create subtasks', completed: false },
          { id: 3, text: 'Assign team members', completed: false }
        ],
        lane: status
      };
      setTasks([...tasks, newTask]);
    };

    return (
      <div
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={`flex-1 min-w-80 rounded-2xl p-4 transition-all duration-500 ${draggedTask ? 'bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-2 border-dashed border-purple-500/50' : ''}`}
        onContextMenu={(e) => {
          e.preventDefault();
          handleLongPress();
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className={`font-bold text-lg bg-gradient-to-r ${darkMode ? 'from-purple-400 to-pink-400' : 'from-purple-600 to-pink-600'} bg-clip-text text-transparent`}>
            {title}
          </h2>
          <span className={`px-3 py-1 rounded-full text-sm font-bold ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'} shadow-lg`}>
            {laneTasks.length}
          </span>
        </div>
        <div className="space-y-3">
          {laneTasks.map(task => (
            <TaskCard key={task.id} task={task} />
          ))}
        </div>
        <button
          onClick={() => {
            setNewTaskLane(status);
            setShowNewTask(true);
          }}
          className="w-full mt-4 py-3 rounded-xl border-2 border-dashed border-gray-300 hover:border-purple-500 hover:bg-purple-500/10 hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-2 group"
        >
          <Plus className="w-5 h-5 text-gray-400 group-hover:text-purple-500 group-hover:rotate-180 transition-all duration-300" />
          <span className={`font-medium ${darkMode ? 'text-gray-400 group-hover:text-purple-400' : 'text-gray-600 group-hover:text-purple-600'} transition-colors`}>
            Add Task
          </span>
        </button>
      </div>
    );
  };

  const filteredTasks = tasks.filter(task => 
    task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    task.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCreateTask = () => {
    if (!newTaskTitle.trim()) return;
    
    const newTask = {
      id: Date.now(),
      title: newTaskTitle,
      description: newTaskDescription || 'New task description',
      assignee: { name: newTaskAssignee, avatar: newTaskAssignee.split(' ').map(n => n[0]).join('') },
      priority: newTaskPriority,
      dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      comments: 0,
      attachments: 0,
      checklist: [
        { id: 1, text: 'First step', completed: false }
      ],
      lane: newTaskLane
    };
    
    setTasks([...tasks, newTask]);
    setNewTaskTitle('');
    setNewTaskDescription('');
    setNewTaskPriority('medium');
    setNewTaskAssignee('Unassigned');
    setShowNewTask(false);
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900' : 'bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50'} transition-all duration-500`}>
      {/* Header */}
      <header className={`${darkMode ? 'bg-gray-800/95' : 'bg-white/95'} backdrop-blur-md shadow-2xl sticky top-0 z-50 border-b border-white/20`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center shadow-lg hover:shadow-2xl transform hover:scale-110 transition-all duration-300">
                <span className="text-white font-bold text-lg">PC</span>
              </div>
              <h1 className={`font-bold text-xl bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent hidden sm:block`}>
                Pocket Command
              </h1>
            </div>
            
            <nav className="hidden md:flex items-center space-x-8">
              <button
                onClick={() => setActiveView('board')}
                className={`font-medium transition-all duration-300 ${activeView === 'board' ? 'text-purple-600 scale-110' : darkMode ? 'text-gray-300 hover:text-white hover:scale-105' : 'text-gray-600 hover:text-gray-900 hover:scale-105'}`}
              >
                Board
              </button>
              <button
                onClick={() => setActiveView('people')}
                className={`font-medium transition-all duration-300 ${activeView === 'people' ? 'text-purple-600 scale-110' : darkMode ? 'text-gray-300 hover:text-white hover:scale-105' : 'text-gray-600 hover:text-gray-900 hover:scale-105'}`}
              >
                People
              </button>
              <button
                onClick={() => setActiveView('search')}
                className={`font-medium transition-all duration-300 ${activeView === 'search' ? 'text-purple-600 scale-110' : darkMode ? 'text-gray-300 hover:text-white hover:scale-105' : 'text-gray-600 hover:text-gray-900 hover:scale-105'}`}
              >
                Search
              </button>
              <button
                onClick={() => setActiveView('analytics')}
                className={`font-medium transition-all duration-300 ${activeView === 'analytics' ? 'text-purple-600 scale-110' : darkMode ? 'text-gray-300 hover:text-white hover:scale-105' : 'text-gray-600 hover:text-gray-900 hover:scale-105'}`}
              >
                Analytics
              </button>
            </nav>

            <div className="flex items-center space-x-4">
              <button
                onClick={() => setDarkMode(!darkMode)}
                className={`p-2 rounded-lg ${darkMode ? 'bg-gray-700 text-yellow-400' : 'bg-gray-100 text-gray-600'} hover:scale-110 transition-all duration-300`}
              >
                {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className={`md:hidden ${darkMode ? 'bg-gray-800' : 'bg-white'} border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="px-4 py-2 space-y-1">
              {['board', 'people', 'search', 'analytics'].map(view => (
                <button
                  key={view}
                  onClick={() => {
                    setActiveView(view);
                    setMobileMenuOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-lg font-medium transition-colors ${activeView === view ? 'text-purple-600 bg-purple-50 dark:bg-purple-900/20' : darkMode ? 'text-gray-300 hover:text-white hover:bg-gray-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`}
                >
                  {view.charAt(0).toUpperCase() + view.slice(1)}
                </button>
              ))}
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeView === 'board' && (
          <div className="flex space-x-6 overflow-x-auto pb-4">
            <Lane
              title="To Do"
              status="todo"
              tasks={filteredTasks.filter(task => task.lane === 'todo')}
            />
            <Lane
              title="In Progress"
              status="inprogress"
              tasks={filteredTasks.filter(task => task.lane === 'inprogress')}
            />
            <Lane
              title="Done"
              status="done"
              tasks={filteredTasks.filter(task => task.lane === 'done')}
            />
          </div>
        )}

        {activeView === 'search' && (
          <div className="max-w-2xl mx-auto">
            <div className="relative mb-8">
              <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search tasks, people, or projects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full pl-10 pr-4 py-3 rounded-xl ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'} shadow-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:scale-105 transition-all duration-300`}
              />
            </div>
            <div className="space-y-4">
              {filteredTasks.map(task => (
                <TaskCard key={task.id} task={task} />
              ))}
            </div>
          </div>
        )}

        {activeView === 'people' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {['Sarah Chen', 'Mike Johnson', 'Alex Kim', 'Emma Davis', 'Tom Wilson', 'Lisa Park'].map((name, i) => (
              <div key={name} className={`${darkMode ? 'bg-gray-800/90' : 'bg-white/95'} backdrop-blur-md rounded-xl p-6 shadow-xl hover:shadow-2xl transform hover:scale-105 hover:-translate-y-1 transition-all duration-300 group`}>
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold shadow-lg group-hover:scale-110 transition-transform">
                    {name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <h3 className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'} group-hover:bg-gradient-to-r group-hover:from-purple-600 group-hover:to-pink-600 group-hover:bg-clip-text group-hover:text-transparent transition-all`}>{name}</h3>
                    <p className="text-sm text-gray-500">Team Member</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">{Math.floor(Math.random() * 10) + 1} tasks</span>
                  <button className="text-purple-600 hover:text-purple-700 font-medium text-sm flex items-center space-x-1 group-hover:translate-x-1 transition-transform">
                    <span>View Profile</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeView === 'analytics' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className={`${darkMode ? 'bg-gray-800/90' : 'bg-white/95'} backdrop-blur-md rounded-xl p-6 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300`}>
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500">
                  <Target className="w-6 h-6 text-white" />
                </div>
                <span className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                  {tasks.length}
                </span>
              </div>
              <p className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Total Tasks</p>
              <p className="text-sm text-gray-500">Across all lanes</p>
            </div>

            <div className={`${darkMode ? 'bg-gray-800/90' : 'bg-white/95'} backdrop-blur-md rounded-xl p-6 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300`}>
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 rounded-lg bg-gradient-to-br from-green-500 to-emerald-500">
                  <CheckSquare className="w-6 h-6 text-white" />
                </div>
                <span className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                  {tasks.filter(t => t.lane === 'done').length}
                </span>
              </div>
              <p className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Completed</p>
              <p className="text-sm text-gray-500">This week</p>
            </div>

            <div className={`${darkMode ? 'bg-gray-800/90' : 'bg-white/95'} backdrop-blur-md rounded-xl p-6 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300`}>
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 rounded-lg bg-gradient-to-br from-yellow-500 to-orange-500">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <span className="text-2xl font-bold bg-gradient-to-r from-yellow-600 to-orange-600 bg-clip-text text-transparent">
                  {tasks.filter(t => t.lane === 'inprogress').length}
                </span>
              </div>
              <p className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>In Progress</p>
              <p className="text-sm text-gray-500">Active now</p>
            </div>

            <div className={`${darkMode ? 'bg-gray-800/90' : 'bg-white/95'} backdrop-blur-md rounded-xl p-6 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300`}>
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
                  92%
                </span>
              </div>
              <p className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Efficiency</p>
              <p className="text-sm text-gray-500">Team average</p>
            </div>
          </div>
        )}
      </main>

      {/* Bottom Navigation */}
      <nav className={`fixed bottom-0 left-0 right-0 ${darkMode ? 'bg-gray-800/95' : 'bg-white/95'} backdrop-blur-md border-t border-gray-200/50 px-4 py-3 md:hidden`}>
        <div className="flex items-center justify-around">
          <button
            onClick={() => setActiveView('board')}
            className={`p-3 rounded-lg transition-all duration-300 ${activeView === 'board' ? 'bg-purple-100 text-purple-600 scale-110' : 'text-gray-600 hover:scale-105'}`}
          >
            <Hash className="w-6 h-6" />
          </button>
          <button
            onClick={() => setShowNewTask(true)}
            className="p-3 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg hover:shadow-2xl transform hover:scale-125 transition-all duration-300"
          >
            <Plus className="w-6 h-6" />
          </button>
          <button
            onClick={() => setActiveView('people')}
            className={`p-3 rounded-lg transition-all duration-300 ${activeView === 'people' ? 'bg-purple-100 text-purple-600 scale-110' : 'text-gray-600 hover:scale-105'}`}
          >
            <Users className="w-6 h-6" />
          </button>
          <button
            onClick={() => setActiveView('search')}
            className={`p-3 rounded-lg transition-all duration-300 ${activeView === 'search' ? 'bg-purple-100 text-purple-600 scale-110' : 'text-gray-600 hover:scale-105'}`}
          >
            <Search className="w-6 h-6" />
          </button>
        </div>
      </nav>

      {/* New Task Modal */}
      {showNewTask && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn">
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-6 max-w-md w-full shadow-2xl transform scale-100 animate-scaleIn`}>
            <h2 className={`text-xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Create New Task
            </h2>
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Task title..."
                value={newTaskTitle}
                onChange={(e) => setNewTaskTitle(e.target.value)}
                className={`w-full px-4 py-3 rounded-lg ${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-purple-500 focus:scale-105 transition-all duration-300`}
              />
              <textarea
                placeholder="Task description..."
                value={newTaskDescription}
                onChange={(e) => setNewTaskDescription(e.target.value)}
                className={`w-full px-4 py-3 rounded-lg ${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-purple-500 focus:scale-105 transition-all duration-300 h-24 resize-none`}
              />
              <select
                value={newTaskPriority}
                onChange={(e) => setNewTaskPriority(e.target.value)}
                className={`w-full px-4 py-3 rounded-lg ${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-purple-500 focus:scale-105 transition-all duration-300`}
              >
                <option value="low">Low Priority</option>
                <option value="medium">Medium Priority</option>
                <option value="high">High Priority</option>
              </select>
              <input
                type="text"
                placeholder="Assignee name..."
                value={newTaskAssignee}
                onChange={(e) => setNewTaskAssignee(e.target.value)}
                className={`w-full px-4 py-3 rounded-lg ${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-purple-500 focus:scale-105 transition-all duration-300`}
              />
            </div>
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowNewTask(false)}
                className={`flex-1 py-3 rounded-lg font-medium ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'} hover:opacity-90 transition-opacity`}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateTask}
                className="flex-1 py-3 rounded-lg font-medium bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:shadow-lg transform hover:scale-105 transition-all duration-300"
              >
                Create Task
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { transform: scale(0.9); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        .animate-scaleIn {
          animation: scaleIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default App;